import { InvitePage } from '../dashboard/pages/InvitePage/InvitePage';
import InviteErrorPage from '../dashboard/pages/InviteErrorPage/InviteErrorPage';
import { Signout } from '../common/components/Signout/Signout';
import { ConnectFBPageToCompanyPage } from '../dashboard/pages/ConnectFBPageToCompanyPage/ConnectFBPageToCompanyPage';
import SettingsPage from '../dashboard/pages/SettingsPage/SettingsPage';
import ChatbotEditorPage from '../editor/pages/ChatbotEditorPage/ChatbotEditorPage';
import OnboardingPage from '../dashboard/pages/SignupOnboarding/common/SignupOnboardingPage/SignupOnboardingPage';
import { RouteType } from './interfaces';
import RedeemPromoCode from '../dashboard/pages/RedeemPromoCode/RedeemPromoCode';
import { LoginPage } from '../dashboard/pages/LoginPage/LoginPage';
import { Subscribe } from '../dashboard/pages/Subscribe/Subscribe';
import { FreeLeadGeneratorSwitch } from '../modules/FreeLeadGenerator/FreeLeadGeneratorSwitch';
import { AutomatedResponseSwitch } from '../modules/FreeLeadGenerator/AutomatedResponseSwitch';
import { ThankYouPage } from '../dashboard/pages/ThankYouPage/ThankYouPage';
import { MasterServiceAgreementPage } from '../modules/PolicyPages/MasterServiceAgreement';
import { PrivacyPolicyPage } from '../modules/PolicyPages/PrivacyPolicy';
import { GDPRPage } from '../modules/PolicyPages/GDPR';
import { EUSpecificPolicyPage } from '../modules/PolicyPages/EUSpecificPolicy';
import { WebsiteReport } from '../dashboard/pages/XRayOnboarding/WebsiteReportSummary/WebsiteReport';
import { WelcomeNonSQL } from '../dashboard/pages/WelcomeNonSQL/WelcomeNonSQL';
import { OptOutPage } from '../modules/OptOutPage/OptOutPage';
import { SelfService } from '../dashboard/pages/SelfService/SelfService';
import { VisitsReport } from '../dashboard/pages/VisitsReportSummary/VisitsReport';

const Routes = {
  login: {
    path: '/login',
    component: LoginPage,
    routeType: RouteType.PublicOnly,
    getLink: () => '/login',
  },
  invite: {
    path: '/invite/:inviteToken',
    component: InvitePage,
    routeType: RouteType.PublicPrivate,
    getLink: (inviteToken: string) => `/invite/${inviteToken}`,
  },
  inviteError: {
    path: '/invite-error',
    component: InviteErrorPage,
    routeType: RouteType.PublicPrivate,
    getLink: () => '/invite-error',
  },
  redeemPromoCode: {
    path: '/redeem-promo-code/:promoCode',
    component: RedeemPromoCode,
    routeType: RouteType.PublicPrivate,
    getLink: (promoCode: string) => `/redeem-promo-code/${promoCode}`,
  },
  signout: {
    path: '/signout',
    component: Signout,
    routeType: RouteType.PrivateOnly,
    getLink: () => '/signout',
  },
  connectPage: {
    path: '/connect-page',
    component: ConnectFBPageToCompanyPage,
    routeType: RouteType.PrivateOnly,
    getLink: () => '/connect-page',
  },
  // Would be good to scope this by company id
  settings: {
    path: '/settings',
    component: SettingsPage,
    routeType: RouteType.PrivateOnly,
    getLink: () => '/settings',
  },

  // TODO: Namespace route under /settings since uses SettingsPage
  settingsUserEdit: {
    path: '/company/:companyId/users/:userId/edit',
    component: SettingsPage,
    routeType: RouteType.PrivateOnly,
    getLink: (companyId: number, userId: number) =>
      `/company/${companyId}/users/${userId}/edit`,
  },
  subscribe: {
    path: '/subscribe/:planCode?',
    component: Subscribe,
    routeType: RouteType.PrivateOnly,
    getLink: (planCode?: string) =>
      planCode ? `/subscribe/${planCode}` : '/subscribe',
  },
  thankYou: {
    path: '/thank-you',
    component: ThankYouPage,
    routeType: RouteType.PrivateOnly,
    getLink: () => '/thank-you',
  },

  selfService: {
    path: '/self-service',
    component: SelfService,
    routeType: RouteType.PublicPrivate,
    getLink: () => '/self-service',
  },

  report: {
    path: '/report',
    component: WebsiteReport,
    routeType: RouteType.PublicPrivate,
    getLink: (websiteURL?: string) =>
      websiteURL ? `/report/${websiteURL}` : '/report',
  },

  visits: {
    path: '/visits',
    component: VisitsReport,
    routeType: RouteType.PublicPrivate,
    getLink: () => '/visits',
  },

  welcomeNonSQL: {
    path: '/welcome',
    component: WelcomeNonSQL,
    routeType: RouteType.PublicPrivate,
    getLink: (websiteURL?: string) =>
      websiteURL ? `/welcome/${websiteURL}` : '/welcome',
  },

  chatbotEditor: {
    path: '/chatbot-editor',
    component: ChatbotEditorPage,
    routeType: RouteType.PrivateOnly,
    getLink: () => '/chatbot-editor',
  },
  signupOnboarding: {
    path: '/onboarding',
    component: OnboardingPage,
    routeType: RouteType.OnboardingParentRoute,
    getLink: () => '/onboarding',
  },
  freeLeadGenerator: {
    path: '/freeleadgenerator',
    component: FreeLeadGeneratorSwitch,
    routeType: RouteType.PublicPrivate,
    getLink: () => '/freeleadgenerator',
  },
  automatedResponseTool: {
    path: '/automatedresponsetool',
    component: AutomatedResponseSwitch,
    routeType: RouteType.PublicPrivate,
    getLink: () => '/automatedresponsetool',
  },
  masterServiceAgreement: {
    path: '/master-service-agreement',
    component: MasterServiceAgreementPage,
    routeType: RouteType.PublicPrivate,
    getLink: () => `${window?.location.origin}/master-service-agreement`,
  },
  privacyPolicy: {
    path: '/privacy-policy',
    component: PrivacyPolicyPage,
    routeType: RouteType.PublicPrivate,
    getLink: () => `${window?.location.origin}/privacy-policy`,
  },
  gdpr: {
    path: '/tocgdpr',
    component: GDPRPage,
    routeType: RouteType.PublicPrivate,
    getLink: () => `${window?.location.origin}/tocgdpr`,
  },
  euSpecificPolicy: {
    path: '/mobilemonkeys-eu-specific-privacy-policy',
    component: EUSpecificPolicyPage,
    routeType: RouteType.PublicPrivate,
    getLink: () =>
      `${window?.location.origin}/mobilemonkeys-eu-specific-privacy-policy`,
  },
  optOut: {
    path: '/opt-out',
    component: OptOutPage,
    routeType: RouteType.PublicPrivate,
    getLink: () => '/opt-out',
  },
};

export { Routes };
